import {
  BufferAttribute,
  BufferGeometry,
  Color,
  InterleavedBufferAttribute,
  Material,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  MathUtils,
  Scene,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  Vector4,
} from "three";
import {
  convertMaterialsToPhysical,
  listMaterialByName,
  listMeshByName,
} from "../util/SceneUtil";
import { LoaderManager } from "./Loader";
import type { SugarAsset, SugarModel } from "./SugarModel";
import { SugarTextureLoader } from "./SugarTextureLoader";
import { renderSettingsStore } from "../core/pipeline/RenderSettingsStore";
import { refreshDualNormal } from "../engine/shader/MultiplMapShaderTSL";
import {
  collectMaterialKeysFromProduct,
  ensureMaterialInfosCached,
  getCachedMaterialRow,
  clearMaterialInfoCache,
} from "./MaterialInfoCache";

function scaleGeometryUvs(
  attributes: Record<string, BufferAttribute | InterleavedBufferAttribute>,
  size: number,
): void {
  if (size === 1) return;

  const scaled = new Set<BufferAttribute | InterleavedBufferAttribute>();
  for (const name in attributes) {
    if (!/^uv\d*$/.test(name)) continue;
    const uv = attributes[name];
    if (!uv || scaled.has(uv)) continue;
    scaled.add(uv);

    if (uv instanceof InterleavedBufferAttribute) {
      const count = uv.count;
      for (let i = 0; i < count; i++) {
        uv.setXY(i, uv.getX(i) * size, uv.getY(i) * size);
      }
    } else {
      const data = uv.array;
      for (let i = 0, n = data.length; i < n; i++) data[i] *= size;
    }
    uv.needsUpdate = true;
  }
}

/**
 * UV'leri `size` ölçeğine getirir. Uygulanan ölçek `geometry.userData.uvScale`'de tutulur;
 * ikinci çağrı yalnızca `yeni / eski` oranıyla çarpar, ölçek katlanmaz.
 * Geometri önbellekle paylaşılıyorsa çağıran önce klonlamalıdır (editör runtime'ı yapar).
 */
export function rescaleGeometryUvs(geometry: BufferGeometry, size: number): void {
  const applied: number = geometry.userData.uvScale ?? 1;
  if (size === applied || !(size > 0)) return;
  scaleGeometryUvs(geometry.attributes, size / applied);
  geometry.userData.uvScale = size;
  geometry.userData.sized = true;
}

/** Catalog `color` 6/8 haneli hex veya #RGBA — Three.Color 8 haneyi yok sayar. */
function parseMaterialColor(raw: string | null | undefined): {
  color: Color;
  alpha?: number;
} {
  if (raw == null || String(raw).trim() === "") {
    return { color: new Color(0xffffff) };
  }
  let h = String(raw).trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (/^[0-9a-fA-F]{8}$/.test(h)) {
    return {
      color: new Color("#" + h.slice(0, 6)),
      alpha: parseInt(h.slice(6, 8), 16) / 255,
    };
  }
  if (/^[0-9a-fA-F]{4}$/.test(h)) {
    return {
      color: new Color("#" + h[0] + h[0] + h[1] + h[1] + h[2] + h[2]),
      alpha: parseInt(h[3] + h[3], 16) / 255,
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(h)) {
    return { color: new Color("#" + h) };
  }
  return { color: new Color("#" + h) };
}

const UP = new Vector3(0, 1, 0);
const FORWARD = new Vector3(0, 0, 1);
const RIGHT = new Vector3(1, 0, 1);

const modelCache: Map<string, SugarModel> = new Map<string, SugarModel>();

const partCache: Map<string, SugarPart> = new Map<string, SugarPart>();

const materialCache: Map<string, SugarMaterial> = new Map<
  string,
  SugarMaterial
>();

/*
 * Base AO ve kumaş AO'su dokuları materyalin yanında WeakMap'te tutulur, userData'da değil:
 * three Material.copy userData'yı JSON ile kopyalar; material.clone() sonrası doku düz nesneye
 * döner ve GLTFExporter onu extras olarak dosyaya yazar. WeakMap materyal silinince kendiliğinden boşalır.
 * (userData.baseNormalMap eskiden beri orada; ortak shader'lar onu okuduğu için taşınmadı.)
 */
const baseAoMaps = new WeakMap<Material, Texture>();
const fabricAoMaps = new WeakMap<Material, Texture>();

/** Parçanın base AO'su (ürüne özel bake edilmiş harita). */
export function getBaseAoMap(material: Material): Texture | undefined {
  return baseAoMaps.get(material);
}

export function setBaseAoMap(material: Material, texture: Texture | null | undefined): void {
  if (texture) baseAoMaps.set(material, texture);
  else baseAoMaps.delete(material);
}

/** Base AO aoMap yuvasındayken kumaşın kendi AO'su (sadece editör modunda dolar). */
export function getFabricAoMap(material: Material): Texture | undefined {
  return fabricAoMaps.get(material);
}

export function setFabricAoMap(material: Material, texture: Texture | null | undefined): void {
  if (texture) fabricAoMaps.set(material, texture);
  else fabricAoMaps.delete(material);
}

/*
 * Base AO mu kumaş AO'su mu aoMap yuvasına girer? Varsayılan eski davranış: kumaşın AO'su
 * varsa base AO'nun üstüne yazar. Oda tasarımı, configurator ve viewer'lar bununla çalışıyor;
 * görüntüleri değişmesin diye yeni davranış sadece ürün editöründe açılır
 * (base AO kalır, kumaş AO'su ayrıca tutulur ve editörün shader'ı ikisini çarpar).
 */
let baseAoWinsOverFabric = false;

export function setBaseAoPrecedence(enabled: boolean): void {
  baseAoWinsOverFabric = enabled;
}

import type {
  MaterialData,
  MaterialRule,
  PartData,
  PartGroupData,
  ProductData,
  ProductFeature,
  ProductFeatureMaterial,
  TextureMap,
} from "../api/types/Product";

export type {
  MaterialData,
  MaterialRule,
  PartData,
  PartGroupData,
  ProductData,
  ProductFeature,
  ProductFeatureMaterial,
  TextureMap,
};

Object3D.prototype.getScreenPosition = function (out: Vector4) {
  return new Vector4();
};

Object3D.prototype.detach = function (): void {
  let pp = this.parent;
  while (pp && !(pp instanceof Scene)) pp = pp.parent;
  if (pp) {
    pp.attach(this);
  } else {
    this.updateMatrixWorld(true);
    const worldMatrix = this.matrixWorld.clone();

    if (this.parent) {
      this.parent.remove(this);
    }
    this.matrix.copy(worldMatrix);
    this.matrix.decompose(this.position, this.quaternion, this.scale);
    this.updateMatrixWorld(true);
  }
};

export const createPart = (partData: PartData) => {
  let part = undefined; //; partCache.get(partData.id);
  if (!part) {
    part = new SugarPart(partData);
    partCache.set(String(partData.id), part);
  }
  return part;
};

export const createMaterial = (materialData: MaterialData, key?: string) => {
  const keyId = key ? key : String(materialData.id);
  let material = materialCache.get(keyId);
  if (!material) {
    material = new SugarMaterial(materialData);
    materialCache.set(keyId, material);
  }
  return material;
};

/** materialCache → servis cache → createMaterial (senkron). */
export const resolveMaterialSync = (
  key: string | number | null | undefined,
): SugarMaterial | undefined => {
  if (key == null || key === "" || key === "0" || key === 0) return undefined;

  const cached = materialCache.get(String(key));
  if (cached) return cached;

  const row = getCachedMaterialRow(key);
  if (row) return createMaterial(row);

  return undefined;
};

function textureMapUri(
  map: TextureMap | string | number | null | undefined,
): string {
  if (!map || typeof map === "number") return "";
  if (typeof map === "string") return map;
  return map.uriKtx ?? map.uri512 ?? map.uri2 ?? map.uri4 ?? map.uri1 ?? "";
}

function packedDiffuseSlots(
  diffuse: MaterialData["diffuse"] | string | undefined,
): string[] {
  return typeof diffuse === "string" ? diffuse.split(";") : [];
}

function hasFabricDiffuse(material: ProductFeatureMaterial): boolean {
  const diffuse = material.diffuse ?? material.diffuseMap;
  if (diffuse && typeof diffuse === "object") {
    return Boolean(textureMapUri(diffuse));
  }
  if (typeof diffuse !== "string" || !diffuse) return false;
  const slots = packedDiffuseSlots(diffuse);
  if (slots.length >= 3) return Boolean(slots[0]);
  return !diffuse.startsWith(";;;");
}

function featureMaterialId(material: ProductFeatureMaterial): number {
  const id = Number(material.id);
  return Number.isFinite(id) ? id : 0;
}

function featureBaseAoMap(material: ProductFeatureMaterial): string {
  if (typeof material.baseAoMap === "string" && material.baseAoMap) {
    return material.baseAoMap;
  }
  const ao = textureMapUri(material.ao);
  if (ao) return ao;
  return packedDiffuseSlots(material.diffuse)[3] ?? "";
}

function featureBaseNormalMap(material: ProductFeatureMaterial): string {
  if (typeof material.baseNormalMap === "string" && material.baseNormalMap) {
    return material.baseNormalMap;
  }
  const normal = textureMapUri(material.normal);
  if (normal) return normal;
  return packedDiffuseSlots(material.diffuse)[2] ?? "";
}

function findDefaultFeature(
  features: ProductFeature[] | null | undefined,
): ProductFeature | undefined {
  if (!features?.length) return undefined;
  return (
    features.find((feature) => (feature.name ?? "").toLowerCase() === "default") ??
    (features.length === 1 ? features[0] : undefined)
  );
}

function partFromFeatureMaterial(
  material: ProductFeatureMaterial,
  index = 0,
): PartData {
  const id = featureMaterialId(material) || -(index + 1);
  const name = material.name || "part";
  return {
    id,
    name,
    code: material.groupBy_ || name,
    title: name,
    orderIndex: 0,
    size: material.size || 1,
    mobileSize: 1,
    oldMobileSize: 1,
    textureSize: 1,
    aoStrength: 1,
    mappingType: 0,
    baseAoMap: featureBaseAoMap(material),
    baseNormalMap: featureBaseNormalMap(material),
    defaultMaterialCode: id ? String(id) : null,
    materialRules: null,
    materials: id ? [id] : null,
  };
}

export type DefaultFeatureVisual = {
  parts: SugarPart[];
  fabrics: Array<{
    fabric: SugarMaterial;
    name: string;
    part: SugarPart;
  }>;
};

type DefaultFeatureGroup = {
  material?: SugarMaterial;
  code: string;
  parts: SugarPart[];
};

function normalizeName(name: string | null | undefined): string {
  return (name ?? "").toUpperCase().trim();
}

/**
 * Default feature malzemeleri. Malzemesini zaten bir group uyguluyorsa
 * (ör. halıda "alt kisim") o malzeme çıkarılır; geri kalanlar (ör. "hali")
 * yine Default feature'dan yüklenir.
 */
function defaultFeatureMaterials(
  data: { features?: ProductFeature[] | null },
  groups?: DefaultFeatureGroup[],
): ProductFeatureMaterial[] {
  const coveredNames = new Set<string>();
  for (const group of groups ?? []) {
    if (!group.material) continue;
    coveredNames.add(normalizeName(group.code));
    for (const part of group.parts) coveredNames.add(normalizeName(part.code));
  }

  const feature = findDefaultFeature(data.features);
  return (feature?.materials ?? []).filter(
    (material) => material && !coveredNames.has(normalizeName(material.name)),
  );
}

export function shouldLoadDefaultFeatureVisual(
  data: { features?: ProductFeature[] | null },
  groups?: DefaultFeatureGroup[],
): boolean {
  return defaultFeatureMaterials(data, groups).length > 0;
}

/**
 * Default feature map'lerini part/group/cache/menüye yazmadan hazırlar.
 * Çağıran `load()` içinde texture'ları çeker, ilk apply'da mesh'e basar.
 */
export function createDefaultFeatureVisual(
  data: { features?: ProductFeature[] | null },
  groups?: DefaultFeatureGroup[],
): DefaultFeatureVisual | null {
  const featureMaterials = defaultFeatureMaterials(data, groups);
  if (!featureMaterials.length) return null;

  const parts: SugarPart[] = [];
  const fabrics: DefaultFeatureVisual["fabrics"] = [];
  for (const [index, material] of featureMaterials.entries()) {
    const part = new SugarPart(partFromFeatureMaterial(material, index));
    parts.push(part);
    if (!hasFabricDiffuse(material)) continue;
    fabrics.push({
      fabric: new SugarMaterial({
        ...material,
        type: material.type ?? "",
      } as MaterialData),
      name: material.name,
      part,
    });
  }
  return { parts, fabrics };
}

export async function applyDefaultFeatureVisual(
  object: Object3D,
  visual: DefaultFeatureVisual,
): Promise<void> {
  for (const part of visual.parts) {
    if (!part.loaded) await part.load();
    await part.apply(object);
  }
  for (const { fabric, name, part } of visual.fabrics) {
    if (!fabric.loaded) await fabric.load();
    const instances = listMaterialByName(object, name);
    for (const instance of instances) {
      if (part.baseNormalMap) {
        instance.userData.baseNormalMap = part.baseNormalMap;
      }
      if (part.baseAoMap) {
        (instance as MeshStandardMaterial).aoMap = part.baseAoMap;
        setBaseAoMap(instance, part.baseAoMap);
      }
      await fabric.apply(instance);
    }
  }
}

/** Ürün materyalleri eksikse `/api/material/by-id` ile batch prefetch. */
export const ensureProductMaterialsCached = async (
  data: {
    partMaterialGroups?: PartGroupData[];
    parts?: PartData[];
    materials?: MaterialData[];
    company?: { id?: number };
  },
  materialDataList: MaterialData[] = [],
): Promise<MaterialData[]> => {
  const keys = collectMaterialKeysFromProduct(data);
  const companyId = data.company?.id;
  await ensureMaterialInfosCached(keys, companyId);

  const merged = [...materialDataList, ...(data.materials ?? [])];
  for (const key of keys) {
    const row = getCachedMaterialRow(key);
    if (!row) continue;
    if (merged.some((material) => String(material.id) === String(row.id))) continue;
    merged.push(row);
  }
  return merged;
};

export const clearCache = () => {
  partCache.clear();
  materialCache.clear();
  clearMaterialInfoCache();
};

export class SugarModelBase extends Object3D {
  public root = new Object3D();

  constructor() {
    super();
    this.root.name = "root";
    this.add(this.root);
  }

  private _freeze = false;
  get freeze() {
    return this._freeze;
  }

  set freeze(value) {
    this._freeze = value;
  }

  private _removeable = false;
  get removeable() {
    return this._removeable;
  }

  set removeable(value) {
    this.removeable = value;
  }

  set configurator(value: SugarModel | undefined) {
    this.userData["configurator"] = value;
  }

  get configurator() {
    return this.userData["configurator"];
  }

  hasIt(object: Object3D, parent?: Object3D) {
    const check = parent ? parent : this;
    if (check == object.parent) return true;
    for (let i = 0; check.children.length > i; i++)
      if (this.hasIt(object, check.children[i])) return true;

    return false;
  }

  dispose() {}
}

/**
 *
 *
 *
 *
 *
 */
export class SugarPart implements SugarAsset {
  private _data: PartData;
  private _isLoaded = false;

  materials: SugarMaterial[] = [];
  baseAoMap: Texture | undefined = undefined;
  baseNormalMap: Texture | undefined = undefined;

  constructor(part: PartData) {
    this._data = part;
    this.materials = (this._data.materials ?? [])
      .map((id) => resolveMaterialSync(id))
      .filter((material): material is SugarMaterial => !!material);
  }

  get data(): PartData {
    return this._data;
  }

  get id(): string {
    return String(this._data.id);
  }

  get name(): string {
    return this._data.name;
  }

  get code(): string {
    return this._data.code;
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl ?? "";
  }

  get size() {
    return this._data.size ? this._data.size : 1;
  }

  get loaded() {
    return this._isLoaded;
  }

  getMaterialRule(material?: SugarMaterial): MaterialRule | undefined {
    if (!material || !Array.isArray(this._data.materialRules)) return undefined;

    return this._data.materialRules.find((rule: MaterialRule) => {
      return String(rule.materialId) === String(material.id);
    });
  }

  async load() {
    if (this._isLoaded) return;

    const loader = new LoaderManager(new SugarTextureLoader());

    const baseAoMap = this._data.baseAoMaterialMap
      ? this._data.baseAoMaterialMap.uriKtx ||
        this._data.baseAoMaterialMap.uri512
      : this._data.baseAoMap;

    const baseNormalMap = this._data.baseNormalMaterialMap
      ? this._data.baseNormalMaterialMap.uriKtx ||
        this._data.baseNormalMaterialMap.uri512
      : this._data.baseNormalMap;

    const partsLooaded = await Promise.all([
      loader.load(baseNormalMap!),
      loader.load(baseAoMap!),
    ]);

    this.baseNormalMap = partsLooaded[0];
    if (this.baseNormalMap) {
      this.baseNormalMap.repeat.x = 1 / this.size;
      this.baseNormalMap.repeat.y = 1 / this.size;
      this.baseNormalMap.flipY = false;
      this.baseNormalMap.needsUpdate = true;
    }

    this.baseAoMap = partsLooaded[1];
    if (this.baseAoMap) {
      this.baseAoMap.repeat.x = 1 / this.size;
      this.baseAoMap.repeat.y = 1 / this.size;
      this.baseAoMap.flipY = false;
      this.baseAoMap.needsUpdate = true;
    }
    this._isLoaded = true;
  }

  async apply(object: Object3D) {
    convertMaterialsToPhysical(object);

    const meshes = listMeshByName(object, this._data.name);
    meshes.forEach((mesh) => {
      const geometry = mesh.geometry;
      mesh.userData.partData = this._data;

      if (geometry.userData.sized) return;

      scaleGeometryUvs(geometry.attributes, this.size);

      geometry.userData.sized = true;
      // Sonradan oranla yeniden ölçeklenebilsin (rescaleGeometryUvs).
      geometry.userData.uvScale = this.size;
      mesh.geometry = geometry;
    });

    const materials = listMaterialByName(object, this._data.name);
    materials.forEach((material) => {
      const mMap = material as MeshStandardMaterial | MeshPhysicalMaterial;
      /*| MeshTransmissionMaterial*/ if (this.baseAoMap) {
        mMap.aoMap = this.baseAoMap;
        setBaseAoMap(mMap, this.baseAoMap);
        this.baseAoMap.flipY = false;
      }
      renderSettingsStore.applyMaterial(mMap);

      if (this.baseNormalMap) {
        mMap.userData.baseNormalMap = this.baseNormalMap;
        this.baseNormalMap.flipY = false;
      }

      refreshDualNormal(mMap);
      mMap.needsUpdate = true;
      //console.log("material id ", mMap.uuid, mMap.id, object.name, object.uuid);
    });
  }
}

export interface SugarModelJointInterface {
  get direction(): Vector3 | string;
  //get up(): Vector3 | string;
  get id(): number;
  get inType(): string;
  get modelId(): string;
  get offset(): Vector3 | string;
  get outType(): string;
  get position(): Vector3 | string;
}

/**
 *
 *
 *
 *
 *
 */
export class SugarMaterial implements SugarAsset {
  private _data: MaterialData;
  private _isLoaded = false;
  private _material?: MeshPhysicalMaterial;

  constructor(data: MaterialData) {
    this._data = data;
  }

  get data(): MaterialData {
    return this._data;
  }
  get id(): string {
    return String(this._data.id);
  }

  get name(): string {
    return this._data.name;
  }

  get code(): string {
    return this._data.groupBy_ ?? "";
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFile ?? "";
  }

  get loaded() {
    return this._isLoaded;
  }

  private _map: Texture | undefined;
  private _normalMap: Texture | undefined;
  private _aoMap: Texture | undefined;
  private _roughnessMap: Texture | undefined;
  private _emissiveMap: Texture | undefined;

  private findMap(
    map: TextureMap | string | number | null | undefined,
  ): string {
    if (!map || typeof map === "string" || typeof map === "number") return "";
    return map.uriKtx ?? map.uri512 ?? map.uri2 ?? map.uri4 ?? map.uri1 ?? "";
  }

  private getMapUris(materailData: MaterialData) {
    let diffuse: any = materailData.diffuse || materailData.diffuseMap || "";

    if (
      diffuse.uri1 ||
      diffuse.uri2 ||
      diffuse.uri4 ||
      diffuse.uri512 ||
      diffuse.uriKtx
    ) {
      diffuse = this.findMap(diffuse);
      diffuse += ";" + this.findMap(this._data.roughness);
      diffuse += ";" + this.findMap(this._data.normal);
      diffuse += ";" + this.findMap(this._data.ao);
      diffuse += ";" + this.findMap(this._data.emissiveMap);
    }

    const mapUris = diffuse.split(";");
    return mapUris;
  }

  async load() {
    if (this._isLoaded) return this;

    const loader = new LoaderManager(new SugarTextureLoader());

    const mapUris = this.getMapUris(this._data);

    const mapLoaded = await Promise.all([
      loader.load(mapUris[0]),
      loader.load(mapUris[1]),
      loader.load(mapUris[2]),
      loader.load(mapUris[3]),
      loader.load(mapUris[4]),
    ]);

    mapLoaded.forEach((m) => {
      if (m) {
        m.flipY = false;
        m.needsUpdate = true;
      }
    });
    this._map = mapLoaded[0];
    this._roughnessMap = mapLoaded[1];
    this._normalMap = mapLoaded[2];
    this._aoMap = mapLoaded[3];
    this._emissiveMap = mapLoaded[4];

    this._isLoaded = true;
    return this;
  }

  private getTextureForApply(texture: Texture, rule?: MaterialRule) {
    return rule ? texture.clone() : texture;
  }

  private applyTextureRule(
    texture: Texture,
    size: number,
    rule?: MaterialRule,
  ) {
    const ruleSize = rule?.size != null ? rule.size : size;

    texture.repeat.x = ruleSize;
    texture.repeat.y = ruleSize;

    if (rule) {
      texture.rotation = MathUtils.degToRad(rule.rotation ?? 0);
      texture.offset.x = rule.offsetX ?? 0;
      texture.offset.y = rule.offsetY ?? 0;
      texture.center.set(0.5, 0.5);
    }

    texture.needsUpdate = true;
  }

  async apply(material: Material, rule?: MaterialRule) {
    const mMap = material as MeshStandardMaterial | MeshPhysicalMaterial;

    // Drop TSL dual-normal override before map swaps (avoids stale texture()).
    (mMap as { normalNode?: unknown | null }).normalNode = null;

    const parsed = parseMaterialColor(this._data.color);
    if (this._data.color != null && String(this._data.color).trim() !== "") {
      mMap.color = parsed.color;
    }

    let opacity = this._data.opacity;
    // Alfa yalnızca 4/8 haneli renk kodundan gelir. Feature opacity 0 "kapalı"
    // demektir; Three.js transparent bayrağını sadece renk alfasından aç.
    const opacityFromColor =
      parsed.alpha != null &&
      (opacity == null || (opacity >= 1 && parsed.alpha < 1));
    if (opacityFromColor && parsed.alpha != null) {
      opacity = parsed.alpha;
    }
    if (opacity != null) mMap.opacity = opacity;
    if (this._data.transparent != null) {
      mMap.transparent =
        this._data.transparent > 0 || (opacityFromColor && mMap.opacity < 1);
    } else if (opacityFromColor) {
      mMap.transparent = mMap.opacity < 1;
    }

    if (this._data.depthTest != null) mMap.depthTest = this._data.depthTest;
    if (this._data.depthWrite != null) mMap.depthWrite = this._data.depthWrite;

    const size = this._data.size ? this._data.size : 1;

    if (this._map) {
      mMap.map = this.getTextureForApply(this._map, rule);
      this.applyTextureRule(mMap.map, size, rule);
      mMap.map.colorSpace = SRGBColorSpace;
    }

    if (this._normalMap) {
      mMap.normalMap = this.getTextureForApply(this._normalMap, rule);
      this.applyTextureRule(mMap.normalMap, size, rule);
      mMap.normalScale = new Vector2(2.5, 2.5);
    }

    if (this._data.metallicFactor != null) {
      mMap.metalness = this._data.metallicFactor;
    }
    if (this._data.roughnessFactor != null) {
      mMap.roughness = this._data.roughnessFactor;
    }
    if (this._roughnessMap) {
      mMap.roughnessMap = this.getTextureForApply(this._roughnessMap, rule);
      mMap.metalnessMap = mMap.roughnessMap;
      this.applyTextureRule(mMap.roughnessMap, size, rule);
    }

    // Editör modunda (setBaseAoPrecedence) ürünün base AO'su kumaşın AO'suna üstün gelir:
    // ikisi de uv kanalı 0'ı kullanır ve materyalde tek aoMap yuvası var. Kumaşın AO'su
    // kaybolmasın diye ayrıca tutulur (getFabricAoMap); editörün baseMapShader'ı ikisini çarpar.
    // Diğer ürünlerde eski davranış: kumaşın AO'su varsa yuvaya o yazılır.
    const baseAoMap = getBaseAoMap(mMap);
    setFabricAoMap(mMap, null);
    if (baseAoWinsOverFabric && baseAoMap) {
      mMap.aoMap = baseAoMap;
      if (this._aoMap) {
        const fabricAoMap = this.getTextureForApply(this._aoMap, rule);
        this.applyTextureRule(fabricAoMap, size, rule);
        setFabricAoMap(mMap, fabricAoMap);
      }
    } else if (this._aoMap) {
      mMap.aoMap = this.getTextureForApply(this._aoMap, rule);
      this.applyTextureRule(mMap.aoMap, size, rule);
    }

    if (this._emissiveMap) {
      mMap.emissiveMap = this.getTextureForApply(this._emissiveMap, rule);
      this.applyTextureRule(mMap.emissiveMap, size, rule);
    }

    renderSettingsStore.applyMaterial(mMap);
    refreshDualNormal(mMap);
    mMap.needsUpdate = true;
  }
}

/**
 *
 *
 *
 *
 */
export class SugarPartGroup implements SugarAsset {
  private _data: PartGroupData;

  private _parts: SugarPart[] = [];
  private _materials: SugarMaterial[] = [];
  private _material?: SugarMaterial;
  private _model?: Object3D;
  private _isLoaded = false;

  get data(): PartGroupData {
    return this._data;
  }
  get id(): string {
    return String(this._data.id);
  }
  get name(): string {
    return this._data.title;
  }
  get code(): string {
    return this._data.code;
  }
  get material(): SugarMaterial | undefined {
    return this._material;
  }
  set material(value: SugarMaterial) {
    if (value === this._material) return;
    this._material = value;
    // Single serialized apply — never fire a second concurrent apply.
    if (this._model) void this.apply(this._model, value);
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl ?? "";
  }

  get materials() {
    return this._materials;
  }

  /** Mesh parts bound to this material zone (for UI labels). */
  get parts() {
    return this._parts;
  }

  get loaded() {
    return this._isLoaded;
  }

  constructor(
    partMaterialGroup: PartGroupData,
    materials: SugarMaterial[],
    parts: SugarPart[],
  ) {
    this._data = partMaterialGroup;

    const code = this._data.code;
    const defaultMaterialCode = this._data.defaultMaterialCode;

    //find default material
    let filteredMaterials = materials?.filter((material) => {
      return (
        material.code?.toUpperCase().trim() === code.toUpperCase().trim() ||
        material.id == defaultMaterialCode
      );
    });

    if (filteredMaterials && filteredMaterials.length > 0) {
      this._material = filteredMaterials[0];
    } else {
      this._material =
        resolveMaterialSync(defaultMaterialCode) ?? resolveMaterialSync(code);
    }

    //find by code parts
    this._parts = parts.filter((part) => {
      return part.code === code || partMaterialGroup.parts?.includes(part.data.id);
    });

    //find possible materials
    this._materials = (this._data.materials ?? [])
      .map((id) => resolveMaterialSync(id))
      .filter((material): material is SugarMaterial => !!material);

    this._parts.forEach((part) => {
      this._materials = [...(part.materials || []), ...this._materials];
    });

    this._materials = this._materials
      .filter((m) => m)
      .reduce((acc: SugarMaterial[], current) => {
        const x = acc.findIndex((item) => item.id === current.id);
        if (x <= -1) {
          acc.push(current);
        }
        return acc;
      }, []);
  }

  async load() {
    await this._material?.load();
    this._isLoaded = true;
  }

  /** Serialize applies so map/node swaps never interleave on WebGPU. */
  private _applyTail: Promise<void> = Promise.resolve();

  async apply(object: Object3D, material?: SugarMaterial): Promise<void> {
    const run = async (): Promise<void> => {
      this._model = object;

      const m = material ? await material.load() : await this._material?.load();
      this._material = m;
      if (!m) return;

      for (const part of this._parts) {
        const materialInstances = listMaterialByName(object, part.name);
        for (const materialInstance of materialInstances) {
          if (part.baseNormalMap) {
            materialInstance.userData.baseNormalMap = part.baseNormalMap;
          }
          if (part.baseAoMap) {
            (materialInstance as MeshStandardMaterial).aoMap = part.baseAoMap;
            setBaseAoMap(materialInstance, part.baseAoMap);
          }
          await m.apply(materialInstance, part.getMaterialRule(m));
        }
      }
    };

    const next = this._applyTail.then(run, run);
    this._applyTail = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }
}
